import type NativeArray from '@ember/array/-private/native-array';
import { on } from '@ember/modifier';
import { dependentKeyCompat } from '@ember/object/compat';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import pick from '@nullvoxpopuli/ember-composable-helpers/helpers/pick';
import { all, restartableTask } from 'ember-concurrency';
import set from 'ember-set-helper/helpers/set';
import { eq, or } from 'ember-truth-helpers';
import PropagableSettingsChangedModalFlowsSearcherGroup from 'garaje/components/propagable-settings-changed/modal/flows-searcher/group';
import LoadingSpinnerPulse from 'garaje/components/ui/classic/loading-spinner-pulse';
import type Flow from 'garaje/models/flow';
import type GlobalSettingBatch from 'garaje/models/global-setting-batch';
import groupBy from 'garaje/utils/decorators/group-by';
import { notEmpty } from 'macro-decorators';

interface PropagableSettingsChangedModalFlowsSearcherSignature {
  Args: {
    selectedChildren: NativeArray<Flow>;
    globalSettingBatch: GlobalSettingBatch;
    close: () => void;
    nextToConfirm: () => void;
    updateChildren: (flows: Flow[]) => void;
  };
}

export default class PropagableSettingsChangedModalFlowsSearcher extends Component<PropagableSettingsChangedModalFlowsSearcherSignature> {
  @service declare store: StoreService;

  @tracked searchQuery: string | null = null;
  @tracked flows: Flow[] = [];

  @notEmpty('args.selectedChildren') atLeastOneSelected!: boolean;

  // TODO: @heroiceric Flows should not be grouped by `location.name` because
  // the group-by addon is not capable of handling async relationships. This
  // only works if the location relationship has already been loaded
  // @ts-expect-error - type issue with groupBy decorator
  @groupBy('filteredFlows', 'location.name') flowsByLocation!: Array<{
    value: string;
    items: Flow[];
  }>;

  constructor(owner: unknown, args: PropagableSettingsChangedModalFlowsSearcherSignature['Args']) {
    super(owner, args);
    void this.fetchFlows.perform();
  }

  @dependentKeyCompat
  get filteredFlows(): Flow[] {
    if (!this.searchQuery) {
      return this.flows;
    }

    return this.flows.filter((flow) => flow.name.toLowerCase().includes(this.searchQuery?.toLowerCase() || ''));
  }

  get allFilteredSelected(): boolean {
    return this.filteredFlows.length <= this.args.selectedChildren.length;
  }

  get selectedMessage(): string {
    const totalFlows = this.flows.length;
    const totalSelected = this.args.selectedChildren.length;

    if (totalSelected === 0) {
      return '';
    } else if (totalSelected === totalFlows) {
      return 'All flows selected';
    } else {
      return `${totalSelected} of ${totalFlows} flows selected`;
    }
  }

  fetchFlows = restartableTask(async () => {
    const flowParentId = this.args.globalSettingBatch.belongsTo('parent').id();
    let flows = (await this.store.findAll('flow')).slice();

    // Remove current flow from the list
    flows = flows.filter((flow) => {
      return flow.id !== flowParentId;
    });

    // Flows cannot be grouped by `location.name` unless we ensure that the
    // location for each flow has been loaded
    await all(flows.map((flow) => flow.location));

    this.flows = flows;
  });

  toggleAllSelected = (): void => {
    const filteredFlows = this.filteredFlows;
    const selectedChildren = this.args.selectedChildren;

    if (this.allFilteredSelected) {
      filteredFlows.forEach((flow) => selectedChildren.removeObject(flow));
    } else {
      filteredFlows.forEach((flow) => selectedChildren.addObject(flow));
    }
  };

  <template>
    <div data-test-propagable-flows-selector class="propagable--flows-selector">
      <header>
        <h2 class="mb-8">
          <span data-test-selector-title>Select locations and sign-in flows</span>
          <button {{on "click" @close}} class="btn btn--small btn--outline btn--cancel float-right box-border">
            &times;
          </button>
        </h2>
        <input
          type="search"
          data-test-searcher-search
          value={{this.searchQuery}}
          class="pageSearch rounded-lg border icon-search"
          placeholder="Search flows..."
          {{on "input" (pick "target.value" (set this "searchQuery"))}}
        />
        <div class="search-options">
          {{#unless this.fetchFlows.isRunning}}
            <input
              type="checkbox"
              data-test-toggle-all
              {{on "click" this.toggleAllSelected}}
              checked={{this.allFilteredSelected}}
            />
          {{/unless}}
          <div class="selected-message" data-test-selected-message>{{this.selectedMessage}}</div>
        </div>
      </header>
      <div
        data-test-flows-list
        class="flows-list {{if (or this.fetchFlows.isRunning (eq this.filteredFlows.length 0)) 'text-centered'}}"
      >
        {{#each this.flowsByLocation as |group|}}
          <PropagableSettingsChangedModalFlowsSearcherGroup
            @group={{group}}
            @selectedChildren={{@selectedChildren}}
            data-test-group-row={{true}}
          />
        {{else}}
          <div data-test-no-match class="flows-list--no-match-flows">
            <div class="relative" style="height: 315px;">
              {{#if this.fetchFlows.isRunning}}
                <LoadingSpinnerPulse data-test-loading />
              {{else}}
                <span class="inline-block mt-32">No match found.</span>
              {{/if}}
            </div>
          </div>
        {{/each}}
      </div>
      <footer class="!p-4 !h-auto text-right">
        <button
          data-test-next-to-confirm
          {{on "click" @nextToConfirm}}
          class="btn btn--brand box-border {{unless this.atLeastOneSelected 'disabled'}}"
        >
          Next
        </button>
      </footer>
    </div>
  </template>
}
